import { useEffect, useState } from 'react'
import { Button, Card, Form, Input, InputNumber, Select, Space, Switch, Typography, message } from 'antd'
import { adminApi } from '../../api'
import { PageHeader } from '../../components/ui'
import '../../styles/admin-settings-cards.css'

type SmsConfig = {
  enabled: boolean
  provider: string
  access_key_id: string
  access_key_secret: string
  sign_name: string
  template_code: string
  region: string
  code_ttl_seconds: number
  send_interval_seconds: number
}

export default function SmsSettings() {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const enabled = Form.useWatch('enabled', form)
  const provider = Form.useWatch('provider', form) || 'mock'

  const loadConfig = () =>
    adminApi.get<SmsConfig>('/admin/settings/sms').then((r) => {
      form.setFieldsValue({
        enabled: r.data.enabled ?? false,
        provider: r.data.provider || 'mock',
        access_key_id: r.data.access_key_id || '',
        access_key_secret: r.data.access_key_secret || '',
        sign_name: r.data.sign_name || '',
        template_code: r.data.template_code || '',
        region: r.data.region || 'cn-hangzhou',
        code_ttl_seconds: r.data.code_ttl_seconds ?? 300,
        send_interval_seconds: r.data.send_interval_seconds ?? 60,
      })
    })

  useEffect(() => {
    loadConfig().catch((e) => message.error(e.message))
  }, [form])

  const save = () => {
    void form.validateFields().then((v) => {
      setLoading(true)
      const payload = { ...v }
      if (!payload.access_key_secret) delete payload.access_key_secret
      adminApi
        .put('/admin/settings/sms', payload)
        .then(() => {
          message.success('短信配置已保存')
          return loadConfig()
        })
        .catch((e) => message.error(e.message))
        .finally(() => setLoading(false))
    })
  }

  return (
    <div>
      <PageHeader title="短信配置" description="开启后，登录页将显示「手机验证码」登录方式。" />
      <Card className="admin-settings-card">
        <Form form={form} layout="vertical">
          <Form.Item name="enabled" label="启用短信登录" valuePropName="checked">
            <Switch />
          </Form.Item>
          {enabled && (
            <>
              <Form.Item name="provider" label="服务商">
                <Select
                  options={[
                    { value: 'mock', label: 'Mock（开发测试，验证码写入后端日志）' },
                    { value: 'aliyun', label: '阿里云短信' },
                  ]}
                />
              </Form.Item>
              {provider === 'aliyun' && (
                <>
                  <Form.Item name="access_key_id" label="AccessKey ID" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="access_key_secret" label="AccessKey Secret">
                    <Input.Password placeholder="不修改请留空" />
                  </Form.Item>
                  <Form.Item name="sign_name" label="短信签名" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="template_code" label="模板 Code" rules={[{ required: true }]}>
                    <Input placeholder="SMS_xxxxxx" />
                  </Form.Item>
                  <Form.Item name="region" label="Region">
                    <Input placeholder="cn-hangzhou" />
                  </Form.Item>
                </>
              )}
              <Form.Item name="code_ttl_seconds" label="验证码有效期（秒）">
                <InputNumber min={60} max={900} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="send_interval_seconds" label="同一手机号发送间隔（秒）">
                <InputNumber min={30} max={300} style={{ width: '100%' }} />
              </Form.Item>
              {provider === 'mock' && (
                <Typography.Paragraph type="secondary">
                  Mock 模式下无需填写密钥；发送验证码后请在后端日志中查看 code（仅用于联调）。
                </Typography.Paragraph>
              )}
            </>
          )}
          <Space>
            <Button type="primary" loading={loading} onClick={save}>
              保存
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
  )
}
